import React from "react";
import { Play, Pause, Heart, Loader2, Music2, Headphones, Flame, ExternalLink, Star } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";

export default function StationCard({ station, onToggleFav, index = 0, nowPlaying, history, offline = false, listeners = 0, trending = false }) {
  const { current, playing, loading, playStation } = usePlayer();
  const isCurrent = current && current.id === station.id;
  const isPlaying = isCurrent && playing;
  const isLink = !!station.external_url;
  const recent = (history || []).filter((h) => !nowPlaying?.title || h.title !== nowPlaying.title).slice(0, 3);

  return (
    <div
      className={`group relative rounded-2xl overflow-hidden border bg-[#141C42] card-hover fade-up ${station.featured ? "border-[#E6B34A] ring-2 ring-[#E6B34A]/60 shadow-[0_0_24px_rgba(230,179,74,0.35)]" : "border-white/5"} ${offline ? "opacity-60" : ""}`}
      style={{ animationDelay: `${index * 60}ms` }}
      data-testid={`station-card-${station.id}`}
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        <img src={station.image_url} alt={station.name} className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${offline ? "grayscale" : ""}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

        <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5">
          {!offline && station.featured && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E6B34A] shadow-[0_0_16px_rgba(230,179,74,0.6)]" data-testid={`featured-badge-${station.id}`}>
              <Star className="w-3 h-3 text-[#0A1030]" fill="currentColor" />
              <span className="text-[10px] font-black tracking-wide text-[#0A1030] uppercase">Featured</span>
            </div>
          )}
          {offline ? (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-700/90 backdrop-blur" data-testid={`offline-badge-${station.id}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400" />
              <span className="text-[10px] font-bold tracking-wide text-zinc-200">OFFLINE</span>
            </div>
          ) : isCurrent && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FF3B30]/90 backdrop-blur">
              <span className="w-1.5 h-1.5 rounded-full bg-white live-dot" />
              <span className="text-[10px] font-bold tracking-wide">LIVE</span>
            </div>
          )}
          {!offline && trending && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#FF7A18] to-[#E6B34A] shadow-[0_0_16px_rgba(230,179,74,0.5)]" data-testid={`trending-badge-${station.id}`}>
              <Flame className="w-3 h-3 text-white" fill="currentColor" />
              <span className="text-[10px] font-black tracking-wide text-white uppercase">Trending</span>
            </div>
          )}
          {!offline && listeners > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur" data-testid={`listeners-badge-${station.id}`}>
              <Headphones className="w-3 h-3 text-[#E6B34A]" />
              <span className="text-[10px] font-bold tracking-wide text-white">{listeners} listening</span>
            </div>
          )}
        </div>

        <button
          onClick={() => onToggleFav && onToggleFav(station)}
          data-testid={`fav-toggle-${station.id}`}
          aria-label="Toggle favorite"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white hover:bg-black/70 transition-colors"
        >
          <Heart className={`w-4 h-4 ${station.is_favorite ? "fill-[#FF3B30] text-[#FF3B30]" : ""}`} />
        </button>

        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="overline text-[#E6B34A] mb-1">{station.genre}</p>
          <div className="flex items-end justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-display font-extrabold text-lg tracking-tight truncate">{station.name}</h3>
              {nowPlaying?.title ? (
                <div className="flex items-center gap-1.5 mt-1 overflow-hidden" data-testid={`nowplaying-${station.id}`}>
                  {nowPlaying.artwork ? (
                    <img src={nowPlaying.artwork} alt="" data-testid={`nowplaying-art-${station.id}`} className="w-5 h-5 rounded object-cover shrink-0 ring-1 ring-white/10" />
                  ) : (
                    <Music2 className="w-3 h-3 text-[#E6B34A] shrink-0" />
                  )}
                  <div className="ticker-mask flex-1 min-w-0">
                    <span className="ticker-text text-xs text-zinc-300">{nowPlaying.title}</span>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-zinc-400 truncate mt-0.5">{station.frequency || "Online"}</p>
              )}
            </div>
            {isLink ? (
              <a
                href={station.external_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`external-link-${station.id}`}
                aria-label={`Open ${station.name}`}
                className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center bg-[#5B9BFF] hover:bg-[#4A87E8] text-white shadow-[0_0_20px_rgba(91,155,255,0.4)] group-hover:scale-105 transition-all"
              >
                <ExternalLink className="w-5 h-5" />
              </a>
            ) : (
            <button
              onClick={() => !offline && playStation(station)}
              disabled={offline}
              data-testid={`play-button-${station.id}`}
              aria-label={offline ? "Offline" : isPlaying ? "Pause" : "Play"}
              className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all ${offline ? "bg-zinc-700 text-zinc-500 cursor-not-allowed" : "bg-[#E6B34A] hover:bg-[#CF9A34] text-[#0A1030] shadow-[0_0_20px_rgba(230,179,74,0.4)] group-hover:scale-105"}`}
            >
              {isCurrent && loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isPlaying ? (
                <Pause className="w-5 h-5" fill="currentColor" />
              ) : (
                <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
              )}
            </button>
            )}
          </div>
        </div>
      </div>
      {recent.length > 0 && !offline && (
        <div className="px-4 py-3 border-t border-white/5 bg-[#0F1638]" data-testid={`recently-played-${station.id}`}>
          <p className="overline text-zinc-500 mb-1.5 flex items-center gap-1.5"><Music2 className="w-3 h-3 text-[#E6B34A]" /> Recently played</p>
          <div className="space-y-1 max-h-24 overflow-y-auto no-scrollbar">
            {recent.map((h, i) => (
              <p key={i} className="text-[11px] text-zinc-400 truncate" title={h.title}>
                <span className="text-zinc-300">{h.artist || h.title}</span>{h.artist && h.song ? <span className="text-zinc-500"> — {h.song}</span> : null}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
